/* System defined core library */
import { ChangeDetectorRef, Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { BootstrapParams } from '../../../../bootstrap-params';
import { RoutingService } from '../../../shared/services/routing.service';
import { TimerService } from '../../../shared/services/timer.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { OrchestrationApiService } from '../../shared/services/orchestration-api.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';
import { Booking } from '../../../shared/models/scheduling/booking.model';
import { Client } from '../../../shared/models/scheduling/client.model';
import { Hold } from '../../../shared/models/scheduling/hold.model';
import { Payment } from '../../../shared/models/scheduling/payment.model';
import { Proctor } from '../../../shared/models/scheduling/proctor.model';
import { Program } from '../../../shared/models/scheduling/program.model';
import { Test } from '../../../shared/models/scheduling/test.model';
import { Timezone } from '../../../shared/models/scheduling/timezone.model';
import { Exam } from '../../../shared/models/scheduling/exam.model';

/* User defined enums/constants */
import { ComponentType, Fee } from '../../../shared/enums/scheduling.enum';
import { ProctorSelectorComponent } from '../../shared/components/proctor-selector/proctor-selector.component';


@Component({
  selector: 'app-reschedule-proctor',
  templateUrl: './reschedule-proctor.component.html',
  styleUrls:['./reschedule-proctor.component.scss']
})
/** reschedule-proctor component */
export class RescheduleProctorComponent {
  @HostListener('window:popstate', ['$event'])
  @ViewChild(ProctorSelectorComponent) proctorSelectorInstance: ProctorSelectorComponent;
  @ViewChild('refreshavailabilitybtn') refreshBtn: ElementRef;

  onPopState(event: any) {
    this.clearDataOnGoingBack();
  }

  /* User defined private/public variables */
  booking: Booking;
  client: Client;
  hasErrorOccured: boolean;
  holdSlotObj: any;
  isSlotSelected: boolean;
  prevSelectedHoldObj: any = [];
  prevSelectedHoldObjArr: any = [];
  program: Program;
  selectedAppointments: AppointmentDetail[] = [];
  selectedIndex: number = 0;
  selectedProctor: Proctor;
  timezone: Timezone;
  hasConflictErrorOccured: boolean;
  examWithTA: Exam[] = []

  /* Service subscriptions */
  booking$: Subscription;
  client$: Subscription;
  prevSelectedHoldObj$: Subscription;
  program$: Subscription;
  selectedProctor$: Subscription;
  selectedAppointments$: Subscription;
  timezone$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private bootstrapParams: BootstrapParams,
    private orchestrationApiService: OrchestrationApiService,
    private routingService: RoutingService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private timerService: TimerService,
    private utilityService: UtilityService,
    private cdRef: ChangeDetectorRef
  ) {
    this.booking$ = selectedExamDetailService.booking.subscribe(
      booking => {
        this.booking = booking;
      });

    this.client$ = this.selectedExamDetailService.client.subscribe(
      client => {
        this.client = client;
      });

    this.prevSelectedHoldObj$ = selectedExamDetailService.prevSelectedHoldObj.subscribe(
      prevSelectedHoldObj => {
        this.prevSelectedHoldObj = prevSelectedHoldObj;
        if (this.prevSelectedHoldObj) {
          this.prevSelectedHoldObjArr = prevSelectedHoldObj;
        }
      });

    this.program$ = this.selectedExamDetailService.program.subscribe(
      program => {
        this.program = program;
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      appts => {
        this.selectedAppointments = appts;
      });

    this.selectedProctor$ = selectedExamDetailService.slot.subscribe(
      slot => {
        this.selectedProctor = slot;
      });

    this.timezone$ = selectedExamDetailService.timezone.subscribe(
      timezone => {
        this.timezone = timezone;
      });

    this.isSlotSelected = false;
    this.selectedIndex = this.selectedExamDetailService.selectedAppointmentIndex;
  }

  /* Initialize the component & call required methods when component loaded first time. */
  ngOnInit() {
    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (this.selectedProctor) {
      this.isSlotSelected = true;
    }
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.RescheduleProctorComponent)) { return; }

    this.selectedAppointments.forEach(
      appt => {
        if (this.utilityService.isNullOrUndefined(appt.exam.hasPayment)) {
          appt.exam.hasPayment = true;
        }
        let tempExamWithTA: any = { id: appt.exam.id, testingAccommodations: appt.exam.testingAccommodations }
        this.examWithTA.push(tempExamWithTA);
      });
  }

  /* Move back a page. */
  goToPrevPage(): void {
    this.clearDataOnGoingBack();
    this.routingService.backwardNavigation(ComponentType.RescheduleProctorComponent);
  }

  /* Use to clear details on navigating backward */
  clearDataOnGoingBack(): void {
    this.selectedExamDetailService.setProctors(null);
    this.selectedExamDetailService.setSlot(null);
    this.selectedExamDetailService.setSelectedStartDate(null);
    this.selectedExamDetailService.setSelectedEndDate(null);
    this.selectedExamDetailService.setHold(null);

    this.timerService.clearClock();

    this.selectedExamDetailService.rescheduleFee = {
      totalAmount: 0,
      currency: null
    };

    this.selectedAppointments.forEach((appointment) => {
      appointment.appointmentSlot = new AppointmentSlot();
      appointment.hold = new Hold();
      appointment.holdId = undefined;
    });

    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
  }

  /* Move to next page */
  goToNextPage() {
    this.isSlotSelected = false; // Only need to disable the next button once clicked (No hide)

    if (this.holdSlotObj) {
      this.schedulingApiService.getSlotCountry(this.selectedAppointments[this.selectedExamDetailService.selectedAppointmentIndex].hold.slot.id).subscribe((response) => {
        this.selectedExamDetailService.proctorCountry = response.location.address.country;
        this.selectedExamDetailService.proctorSiteId = response.id;
        this.getExamPaymentDetails();
        this.holdSlot(this.holdSlotObj);
      });
    } else if (this.prevSelectedHoldObjArr.length > 0) {
      this.routingService.rescheduleFwdNavigation(ComponentType.RescheduleProctorComponent);
    }
  }

  // TODO - move these methods to service - KJC
  getExamPaymentDetails(): void {
    let totalAmount: number = 0;
    let currencyCode: string = '';

    this.selectedAppointments.forEach(
      (appointmentDetail) => {
        appointmentDetail.payments = [];
        this.schedulingApiService.getBookingPaymentFeeDetail(appointmentDetail.id, appointmentDetail.exam.id).subscribe(
          paymentFee => {
            paymentFee.forEach(payment => {
              if (payment.type === Fee.RescheduleFee) {
                let testData: Test = this.booking.tests.find((test) => {
                  return test.exam.id === appointmentDetail.exam.id;
                });

                const tmpPayment: Payment = new Payment(
                  !testData.cnatt ? payment.amount : 0,
                  payment.currency,
                  Fee.RescheduleFee
                );
                totalAmount += tmpPayment.amount;
                currencyCode = tmpPayment.currency;
                appointmentDetail.payments.push(tmpPayment);
                this.selectedExamDetailService.rescheduleFee = {
                  totalAmount: totalAmount,
                  currency: currencyCode
                };

                this.selectedExamDetailService.isPaymentRequired = this.selectedAppointments.some(appt => appt.exam.hasPayment) && totalAmount > 0;

                this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

                if (!appointmentDetail.exam.hasPayment) {
                  this.clearPaymentDetails(appointmentDetail.exam.id);
                }
              }
            });
          });
      });
  }

  // TODO - move these methods to service - KJC
  clearPaymentDetails(examId: any) {
    let totalAmount = 0;
    this.selectedAppointments.forEach(
      (appointmentDetail) => {
        if (appointmentDetail.exam.id === examId) {
          appointmentDetail.payments.forEach((payment) => {
            if (payment) {
              payment.amount = 0;
            } else {
              appointmentDetail.payments.push(new Payment(0));
            }
          });
        }
      });

    this.selectedAppointments.forEach(appt => totalAmount += appt.payments.length ? appt.payments[0].amount : 0);
    this.selectedExamDetailService.rescheduleFee['totalAmount'] = totalAmount;

    this.selectedExamDetailService.isPaymentRequired = this.selectedAppointments.some(appt => appt.exam.hasPayment) && totalAmount > 0;
    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
  }

  // TODO - move these methods to service - KJC
  setSettingsFromFlows(response: any, examId?: string) {
    const notificationResp = response.filter(function (el: any) {
      return el.name === 'Verification';
    });

    this.selectedExamDetailService.showTextReminder = notificationResp[0].data &&
      notificationResp[0].data.length &&
      notificationResp[0].data[0].parameters.smsText === 'True' ? true : false;

    const paymentResp = response.filter(function (el: any) {
      return el.name === 'Payment';
    });

    examId = examId || this.selectedAppointments[this.selectedExamDetailService.selectedAppointmentIndex].exam.id;

    this.selectedAppointments.forEach(
      appt => {
        if (appt.exam.id === examId) {
          appt.exam.hasPayment = paymentResp.length && paymentResp[0].settings.reschedule === 'True';
        }
      });

    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

    this.selectedExamDetailService.isBiometricConsentRequiredReschedule = !this.selectedExamDetailService.isBiometricConsentSavedInSchedule;

    // To hide Biometric consent for the clients listed in the config
    if (this.bootstrapParams.hideBiometricsClients.indexOf(this.client.code.toUpperCase()) !== -1) {
      this.selectedExamDetailService.isBiometricConsentRequiredReschedule = false;
    }

    this.selectedAppointments.forEach(
      appt => {
        if (!appt.exam.hasPayment) {
          this.clearPaymentDetails(appt.exam.id);
        }
      });
  }

  /* disable next button on the basis of local variable value */
  public isDisable(): boolean {
    return !this.isSlotSelected;
  }

  public setSelectedSiteDetails(selectedSiteDetails: any): void {
    this.selectedAppointments = selectedSiteDetails.selectedAppointments;
    this.selectedIndex = selectedSiteDetails.selectedIndex;
    this.holdSlotObj = selectedSiteDetails.holdSlotObj;
    this.hasErrorOccured = false;
  }

  public setSlotsCheck(slotsCheck: any): void {
    this.hasConflictErrorOccured = false;
    this.isSlotSelected = slotsCheck;
  }

  refreshSlotAvailability(): void {
    this.proctorSelectorInstance.getAvailableTimes();
  }

  // TODO - move these methods to service - KJC
  holdSlot(holdSlot: Hold): void {
    this.selectedIndex = this.selectedExamDetailService.selectedAppointmentIndex;

    this.schedulingApiService.holdProctor(this.selectedAppointments[this.selectedIndex].hold, this.timezone.code, this.examWithTA).subscribe((holdsObjects: any) => {
      if (holdsObjects.length) {
        this.selectedAppointments.forEach((appt: AppointmentDetail, index: number) => {
          appt.status = 'schedule';
          appt.hold = holdsObjects[index];
          appt.holdId = holdsObjects[index].id;
          holdSlot.id = holdsObjects[index].id;
          this.prevSelectedHoldObjArr.push(holdSlot);
          this.selectedAppointments[index].hold = holdsObjects[index];

          if (index === 0) {
            this.selectedAppointments[index].appointmentSlot.startDateTime = new Date(this.utilityService.getFormatToFullMMDDYYYY(appt.appointmentSlot.startDateTime, '/'));
            this.selectedExamDetailService.setHold(holdSlot);
          } else {
            this.selectedAppointments[index].appointmentSlot.testTime = this.selectedAppointments[index - 1].appointmentSlot.testEndTime;
            this.selectedAppointments[index].appointmentSlot.duration = this.selectedAppointments[index].exam.duration;
            this.selectedAppointments[index].appointmentSlot.startDateTime = UtilityService.addMins(
              this.selectedAppointments[index - 1].appointmentSlot.startDateTime,
              this.selectedAppointments[index - 1].appointmentSlot.duration
            );
            this.selectedAppointments[index].appointmentSlot.testEndTime = this.utilityService.getStartEndFormat(
              UtilityService.addMins(
                this.selectedAppointments[index].appointmentSlot.startDateTime,
                this.selectedAppointments[index].appointmentSlot.duration
              )
            );
          }
        });

        this.selectedExamDetailService.setprevSelectedHoldObj(this.prevSelectedHoldObjArr);

        this.selectedAppointments[this.selectedIndex].appointmentSlot.id = this.holdSlotObj.slot.id;
        this.selectedAppointments[this.selectedIndex].appointmentSlot.href = this.holdSlotObj.slot.uri;
        this.selectedExamDetailService.SetSlotDataSource(this.selectedAppointments[this.selectedIndex].appointmentSlot);
        this.selectedExamDetailService.isRescheduleSlotSelected = true;

        this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);


        if (this.selectedExamDetailService.isBackToBackRequired) {
          this.orchestrationApiService.getExamSetFlows(
            this.client.code,
            this.program.code,
            this.selectedExamDetailService.examSetId,
            this.selectedExamDetailService.proctorCountry
          ).subscribe((resp: any[]) => {
            let examsOutOfSet = 0;
            this.selectedAppointments.forEach(appointment => {
              if (!resp.find((flow) => { return flow.id === appointment.exam.id })) {
                examsOutOfSet++;
                this.setSettingsFromFlows(resp, appointment.exam.id);
                this.routingService.rescheduleFwdNavigation(ComponentType.RescheduleTestCenterComponent);
              }
            });
            resp.forEach((flow) => {
              this.setSettingsFromFlows(flow.flowSet, flow.id);
              if (!examsOutOfSet) {
                this.routingService.rescheduleFwdNavigation(ComponentType.RescheduleTestCenterComponent);
              }
            });
          })
        } else {
          // retrieving consent for first exam
          this.orchestrationApiService.getFlows(
            this.client.code,
            this.program.code,
            this.selectedAppointments[this.selectedIndex].exam.code,
            this.selectedExamDetailService.proctorCountry
          ).subscribe((resp) => {
            this.setSettingsFromFlows(resp);
            this.routingService.rescheduleFwdNavigation(ComponentType.RescheduleTestCenterComponent);
          });
        }
      } else {
         if(holdsObjects == -2)
         {
            this.hasErrorOccured = true;
            this.hasConflictErrorOccured = true;
            this.cdRef.detectChanges();
            this.isSlotSelected = false;
            this.refreshBtn.nativeElement.focus();
         }
         else {
            this.hasErrorOccured = true;
            this.isSlotSelected = false;
         }
      }
    });
  }
}
